import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { PrismIconComponent, PrismMenuComponent, PrismMenuItemComponent } from '@shure/prism-angular-components';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';
import { FirmwarePackage, isLatestFirmwarePackage } from '@shure/shared/models';

export interface FirmwareVersionDeviceCellData {
	id: string;
	currentVersion: string;
	currentKey?: string;
	availablePackages: FirmwarePackage[];
	isValid: boolean;
	canUpdate?: boolean;
	showLatestIndicator?: boolean;
	/** set to true if this firwmare is the hosted Firmare, opposed to "primary" firmware */
	isHostedFirmware?: boolean;
}

export interface DeviceFwVersionRendererParent {
	upgradeFirmware(deviceId: string, pkg: FirmwarePackage, isHostedFirmwarePackage: boolean): void;
}

interface DeviceFwVersionRendererComponentContext {
	componentParent: DeviceFwVersionRendererParent;
}

export type DeviceFwVersionRendererComponentParamsType = ICellRendererParams<
	object,
	FirmwareVersionDeviceCellData,
	DeviceFwVersionRendererComponentContext
>;

@Component({
	selector: 'sh-device-fw-version-renderer',
	imports: [CommonModule, PrismIconComponent, PrismMenuComponent, PrismMenuItemComponent, TranslocoModule],
	templateUrl: './device-fw-version-renderer.component.html',
	styleUrls: ['./device-fw-version-renderer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
@shUseTranslation('shAgGrid', /* istanbul ignore next */ (lang) => import(`../../../i18n/${lang}`))
export class DeviceFwVersionRendererComponent implements ICellRendererAngularComp {
	protected readonly firmwareInfo = signal<FirmwareVersionDeviceCellData | null | undefined>(undefined);

	protected readonly currentVersion = computed(() => {
		const info = this.firmwareInfo();

		if (!info?.currentVersion) {
			return '';
		}

		// if a hostedFW Cell, append the package key if present in paranthesis to the version.
		// e.g. 1.1.1.1 (ADTQ)
		const key =
			info?.isHostedFirmware && info?.currentKey && info.currentKey.length > 0 ? `(${info.currentKey})` : '';
		const versionWithKey = `${info.currentVersion} ${key}`.trim();

		// Append the * character if it's defined and not valid
		return `${versionWithKey}${info.isValid ? '' : '*'}`;
	});
	protected readonly availablePackages = computed(() => this.firmwareInfo()?.availablePackages ?? []);
	protected readonly isLatestVersion = computed(() =>
		isLatestFirmwarePackage(this.firmwareInfo()?.currentVersion, this.availablePackages())
	);
	protected readonly canUpdate = computed(() => {
		const info = this.firmwareInfo();
		return info?.canUpdate === undefined ? true : info.canUpdate;
	});
	protected readonly showLatestIndicator = computed(() => {
		const info = this.firmwareInfo();
		return info?.showLatestIndicator === undefined ? true : info.showLatestIndicator;
	});

	protected readonly showPackageKey = computed(() => {
		const info = this.firmwareInfo();
		return Boolean(info?.isHostedFirmware);
	});

	protected readonly shouldRender = computed(() => this.firmwareInfo() ?? false);

	public params!: DeviceFwVersionRendererComponentParamsType;

	constructor(public readonly transloco: TranslocoService) {}

	public agInit(params: DeviceFwVersionRendererComponentParamsType): void {
		this.params = params;
		this.firmwareInfo.set(params.value);
	}

	public refresh(params: DeviceFwVersionRendererComponentParamsType): boolean {
		this.params = params;
		this.firmwareInfo.set(params.value);
		return true;
	}

	public onMenuSelection(event: FirmwarePackage): void {
		this.params.context.componentParent.upgradeFirmware(
			this.params.value?.id ?? '',
			event,
			this.firmwareInfo()?.isHostedFirmware ?? false
		);
	}
}
